import { Component, OnDestroy, OnInit, ElementRef, ViewChild, AfterViewInit } from '@angular/core';
import { Subscription } from 'rxjs';
import { ControleCronService, CronStatus } from '../../services/requests/controle-cron.service';
import { ModalClickOutsideService } from '../../services/modal-click-outside.service';

declare function showNotif(message: string, type?: string): void;

@Component({
  selector: 'app-controle-cron',
  templateUrl: './controle-cron.component.html',
  styleUrls: ['./controle-cron.component.css']
})
export class ControleCronComponent implements OnInit, OnDestroy, AfterViewInit {
  crons: CronStatus[] = [];
  filtered: CronStatus[] = [];
  isLoading = false;
  errorMessage = '';
  lastRefresh?: number;
  showAddCronModal = false;
  isAdding = false;
  cron: { name: string; usageInterval: number | null } = {
    name: '',
    usageInterval: null
  };

  // Tri
  sortField: string = 'name';
  sortDirection: 'asc' | 'desc' = 'asc';

  private _searchTerm: string = '';
  get searchTerm(): string {
    return this._searchTerm;
  }
  set searchTerm(value: string) {
    this._searchTerm = value;
    this.applyFilter();
  }

  private cronSubscription?: Subscription;

  @ViewChild('addCronModalContent', { static: false }) addCronModalContent!: ElementRef;

  constructor(
    private controleCronService: ControleCronService,
    private modalClickOutsideService: ModalClickOutsideService
  ) {}

  ngOnInit(): void {
    this.isLoading = true;
    this.cronSubscription = this.controleCronService.cronStatuses$.subscribe(crons => {
      this.crons = crons;
      this.applyFilter();
      this.lastRefresh = Date.now();
      this.isLoading = false;
    });

    this.reloadCrons();
    this.controleCronService.startAutoRefresh();
  }

  ngAfterViewInit(): void {
    this.updateModalRegistration();
  }

  private updateModalRegistration(): void {
    if (this.addCronModalContent) {
      if (this.showAddCronModal) {
        this.modalClickOutsideService.registerModal(this.addCronModalContent, () => this.closeAddCronModal());
      } else {
        this.modalClickOutsideService.unregisterModal(this.addCronModalContent);
      }
    }
  }

  ngOnDestroy(): void {
    this.cronSubscription?.unsubscribe();
    if (this.addCronModalContent) {
      this.modalClickOutsideService.unregisterModal(this.addCronModalContent);
    }
  }

  reloadCrons(): void {
    this.isLoading = true;
    this.errorMessage = '';

    this.controleCronService.refreshCache().subscribe({
      next: () => {
        this.isLoading = false;
      },
      error: (error) => {
        this.isLoading = false;
        this.errorMessage = error?.message || 'Impossible de charger les cron.';
      }
    });
  }

  trackByCron(_index: number, cron: CronStatus): string {
    return cron._id || cron.name;
  }

  isCronLate(cron: CronStatus): boolean {
    return this.controleCronService.isCronLate(cron);
  }

  getStatusLabel(cron: CronStatus): string {
    return this.isCronLate(cron) ? 'En retard' : 'À jour';
  }

  getElapsedLabel(cron: CronStatus): string {
    if (!cron.timestamp) {
      return 'Jamais';
    }
    return this.formatDuration(this.controleCronService.getTimeSinceUpdate(cron));
  }

  getUsageIntervalLabel(cron: CronStatus): string {
    if (!cron.usageInterval) {
      return 'N/C';
    }
    return this.formatDuration(cron.usageInterval);
  }

  private formatDuration(durationMs: number): string {
    if (durationMs <= 0) {
      return '0 s';
    }

    const totalSeconds = Math.floor(durationMs / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);

    if (days > 0) {
      return `${days} j ${(hours % 24)} h`;
    }
    if (hours > 0) {
      return `${hours} h ${(minutes % 60)} min`;
    }
    if (minutes > 0) {
      return `${minutes} min ${seconds.toString().padStart(2, '0')} s`;
    }
    return `${seconds} s`;
  }

  openAddCronModal(): void {
    this.resetCronForm();
    this.showAddCronModal = true;
    // Attendre que le DOM soit mis à jour avant d'enregistrer le modal
    setTimeout(() => {
      this.updateModalRegistration();
    }, 0);
  }

  closeAddCronModal(): void {
    this.showAddCronModal = false;
    this.resetCronForm();
    this.updateModalRegistration();
  }

  resetCronForm(): void {
    this.cron = {
      name: '',
      usageInterval: null
    };
  }

  addCron(): void {
    const { name, usageInterval } = this.cron;

    if (!name || !usageInterval || usageInterval < 1000) {
      showNotif("Veuillez remplir tous les champs requis. L'intervalle doit être d'au moins 1000ms (1 seconde).", 'info');
      return;
    }

    this.isAdding = true;

    const cronToSend = {
      name: name.trim(),
      timestamp: Date.now(),
      usageInterval: usageInterval
    };

    this.controleCronService.add(cronToSend).subscribe({
      next: (response: any) => {
        this.isAdding = false;
        if (response.status) {
          showNotif('✅ Cron ajouté avec succès', 'success');
          this.closeAddCronModal();
          this.reloadCrons();
        } else {
          showNotif(response.message || '❌ Erreur lors de l\'ajout du cron', 'error');
        }
      },
      error: (error: any) => {
        this.isAdding = false;
        console.error('❌ Erreur addCron:', error);
        showNotif(error?.error?.message || '❌ Erreur serveur lors de l\'ajout du cron', 'error');
      }
    });
  }

  toggleSort(field: string): void {
    if (this.sortField === field) {
      this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortField = field;
      this.sortDirection = 'asc';
    }
    this.applyFilter();
  }

  applyFilter(): void {
    const term = this._searchTerm.toLowerCase().trim();
    let base = [...this.crons];

    if (term) {
      base = base.filter(cron => 
        cron.name.toLowerCase().includes(term)
      );
    }

    // Tri
    base.sort((a, b) => {
      let valueA: any;
      let valueB: any;
      
      switch (this.sortField) {
        case 'name':
          valueA = a.name?.toLowerCase() || '';
          valueB = b.name?.toLowerCase() || '';
          return this.sortDirection === 'asc' 
            ? valueA.localeCompare(valueB) 
            : valueB.localeCompare(valueA);
        
        case 'intervalle':
          valueA = a.usageInterval || 0;
          valueB = b.usageInterval || 0;
          return this.sortDirection === 'asc' 
            ? valueA - valueB 
            : valueB - valueA;
        
        case 'timestamp':
          valueA = a.timestamp || 0;
          valueB = b.timestamp || 0;
          return this.sortDirection === 'asc' 
            ? valueA - valueB 
            : valueB - valueA;
        
        default:
          return 0;
      }
    });

    this.filtered = base;
  }
}

